import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useUser } from "../context/useUser";
import { apiFetch } from "../api/client";

const url = process.env.REACT_APP_BACKEND_CONNECTION;

function formatStatusMessage(data) {
  let message = data.message || "Dependency import completed.";

  if (data.created?.length > 0) {
    message += `\nCreated dependencies:\n${data.created
      .map((item) => `${item.option_name}${item.option_category ? ` [${item.option_category}]` : ""} -> ${item.depends_on_option_name}${item.depends_on_option_category ? ` [${item.depends_on_option_category}]` : ""} (${item.housing_company})`)
      .join("\n")}`;
  }

  if (data.skipped?.length > 0) {
    message += `\nSkipped dependencies:\n${data.skipped
      .map((item) => `${item.option_name}${item.option_category ? ` [${item.option_category}]` : ""} -> ${item.depends_on_option_name}${item.depends_on_option_category ? ` [${item.depends_on_option_category}]` : ""} (${item.housing_company})`)
      .join("\n")}`;
  }

  if (data.errors?.length > 0) {
    message += `\nErrors:\n${data.errors
      .map((item) => `${item.error} — ${JSON.stringify(item.row)}`)
      .join("\n")}`;
  }

  return message;
}

export default function AdminOptionDependencies() {
  const { user } = useUser();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [dependencies, setDependencies] = useState([]);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user?.token) {
      return;
    }

    async function loadCompanies() {
      setLoadingCompanies(true);

      try {
        const data = await apiFetch("/admin/housing-companies", {
          token: user.token,
        });

        setCompanies(data);

        if (data.length > 0) {
          setSelectedCompanyId((current) => current || String(data[0].housing_company_id));
        }
      } catch (error) {
        console.error("Failed to load housing companies:", error);
        setStatus(error.body?.error || "Failed to load housing companies.");
      } finally {
        setLoadingCompanies(false);
      }
    }

    loadCompanies();
  }, [user]);

  useEffect(() => {
    if (!user?.token || !selectedCompanyId) {
      setDependencies([]);
      return;
    }

    async function loadDependencies() {
      setLoadingDependencies(true);

      try {
        const data = await apiFetch(`/admin/housing-company/${selectedCompanyId}/option-dependencies`, {
          token: user.token,
        });

        setDependencies(data);
      } catch (error) {
        console.error("Failed to load dependencies:", error);
        setStatus(error.body?.error || "Failed to load dependencies.");
      } finally {
        setLoadingDependencies(false);
      }
    }

    loadDependencies();
  }, [selectedCompanyId, user]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      setStatus("Please select a CSV file first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    setStatus("");

    try {
      const response = await axios.post(
        `${url}/admin/housing-company/import-option-dependencies`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setStatus(formatStatusMessage(response.data));
      setFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (selectedCompanyId) {
        const updatedDependencies = await apiFetch(
          `/admin/housing-company/${selectedCompanyId}/option-dependencies`,
          { token: user.token }
        );
        setDependencies(updatedDependencies);
      }
    } catch (error) {
      console.error("Dependency import error:", error);
      setStatus(error.response?.data?.error || "Something went wrong during dependency import.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (dependencyId) => {
    if (!window.confirm("Poistetaanko tämä riippuvuus?")) {
      return;
    }

    setRemovingId(dependencyId);
    setStatus("");

    try {
      await apiFetch(`/admin/option-dependency/${dependencyId}`, {
        method: "DELETE",
        token: user.token,
      });

      setDependencies((current) => current.filter((item) => item.id !== dependencyId));
      setStatus("Dependency removed successfully.");
    } catch (error) {
      console.error("Failed to delete dependency:", error);
      setStatus(error.body?.error || "Failed to delete dependency.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="default-container">
      <div className="default-box">
        <h2 className="default-title">
          <i className="fas fa-link" /> Hallinnoi valintariippuvuuksia
        </h2>

        <p className="default-text">
          Lisää riippuvuuksia CSV-tiedostolla tai poista olemassa olevia riippuvuuksia valitusta taloyhtiöstä.
        </p>

        <form onSubmit={handleSubmit} className="admin-form">
          <label className="admin-label">
            <i className="fas fa-file-csv" /> Valitse CSV Tiedosto:
          </label>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
            className="admin-input"
          />

          <button className="default-button" type="submit" disabled={uploading}>
            {uploading ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Ladataan...
              </>
            ) : (
              <>
                <i className="fas fa-upload" /> Lisää riippuvuudet
              </>
            )}
          </button>

          <label className="admin-label" htmlFor="dependency-company-select">
            <i className="fas fa-building" /> Valitse taloyhtiö:
          </label>

          <select
            id="dependency-company-select"
            className="admin-input"
            value={selectedCompanyId}
            onChange={(event) => setSelectedCompanyId(event.target.value)}
            disabled={loadingCompanies || companies.length === 0}
          >
            {companies.length === 0 ? (
              <option value="">Ei taloyhtiöitä</option>
            ) : (
              companies.map((company) => (
                <option
                  key={company.housing_company_id}
                  value={company.housing_company_id}
                >
                  {company.company_name}
                </option>
              ))
            )}
          </select>
        </form>

        <div className="admin-dependency-table-wrapper">
          {loadingDependencies ? (
            <p className="default-text">Ladataan riippuvuuksia...</p>
          ) : dependencies.length === 0 ? (
            <p className="default-text">Valitulle taloyhtiölle ei ole tallennettu riippuvuuksia.</p>
          ) : (
            <div className="admin-dependency-list">
              {dependencies.map((dependency) => (
                <div key={dependency.id} className="admin-dependency-item">
                  <div className="admin-dependency-content">
                    <div className="admin-dependency-copy">
                      <p className="default-text admin-dependency-label">Valinta</p>
                      <p
                        className="default-text admin-dependency-value"
                        title={dependency.option_name}
                      >
                        {dependency.option_name}
                      </p>
                    </div>

                    <div className="admin-dependency-copy">
                      <p className="default-text admin-dependency-label">Estyy valinnasta</p>
                      <p
                        className="default-text admin-dependency-value"
                        title={dependency.depends_on_option_name}
                      >
                        {dependency.depends_on_option_name}
                      </p>
                    </div>
                  </div>

                  <p
                    className="cart-delete-button"
                    onClick={() => {
                      if (removingId !== dependency.id) {
                        handleDelete(dependency.id);
                      }
                    }}
                    aria-label="Poista riippuvuus"
                    title={removingId === dependency.id ? "Poistetaan..." : "Poista riippuvuus"}
                  >
                    <i className={`fa-solid ${removingId === dependency.id ? "fa-spinner fa-spin" : "fa-xmark"}`} />
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {status && (
          <pre
            className={`status-text ${
              status.toLowerCase().includes("error") ||
              status.toLowerCase().includes("failed") ||
              status.toLowerCase().includes("please")
                ? "error"
                : "success"
            }`}
            style={{ whiteSpace: "pre-wrap", textAlign: "left" }}
          >
            {status}
          </pre>
        )}
      </div>
    </div>
  );
}